import {
    isSlotEditable,
    validationColoring,
    standardColoring,
    getSlotContent,
    getSlotTitle
} from "../utils/plasmid";

const sizeToValue = (size) => {
    return {
        small: 0.2,
        medium: 1.,
        large: 4.
    }[size];
}

const plasmid = () => {
    let width,
        height,
        margin = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
        },
        padAngle, // effectively dictates the gap between slices
        title,
        dataUrl = '',
        slotOptions,
        current,
        editable = false,
        outerRadius = 1.0,
        innerRadius = 0.8,
        validationResults;

    function chart(selection) {
        selection.each(function (data) {
                // generate chart
                // Set up constructors for making donut. See https://github.com/d3/d3-shape/blob/master/README.md
                let radius = Math.min(width, height) / 2.01,
                    halfPi = Math.PI / 2,
                    cos = Math.cos,
                    sin = Math.sin,
                    r0 = radius * innerRadius,
                    r1 = radius * outerRadius,
                    rmid = (r0 + r1) / 2;

                // creates a new pie generator
                let pie = d3.pie()
                    .value(function (d) {
                        return sizeToValue(d["size"]);
                    })
                    .sort(null);

                let arc = d3.arc()
                    .outerRadius(r1)
                    .innerRadius(r0);

                // append the svg object to the selection
                let svg = selection.append('svg')
                    .attr('width', width + margin.left + margin.right)
                    .attr('height', height + margin.top + margin.bottom)
                    .attr('class', 'd-block')
                    .append('g')
                    .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

                svg.append("circle")
                    .attr("class", data.length === 0 ? 'stroke-primary-light' : '')
                    .attr("cx", 0)
                    .attr("cy", 0)
                    .attr("r", rmid);

                if (title)
                    svg.append('text')
                        .style('text-anchor', 'middle')
                        .html(`<tspan>${title}</tspan>`)

                const toggleModal = slotOptions?.toggleModal

                svg.append('g').attr('class', 'slices');
                svg.select('.slices').selectAll('path')
                    .data(pie)
                    .enter()
                    .append('a')
                    .attr(toggleModal ? 'hx-get' : 'xlink:href', function (d) {
                        if (slotOptions.notClickable)
                            return
                        if (isSlotEditable(d.data, slotOptions)) {
                            if (slotOptions.prefixUrl && slotOptions.action)
                                return slotOptions.prefixUrl + 'slots/' + d.data.id + '/' + slotOptions.action;
                        }
                        return d.data.urls.view
                    })
                    .attr('data-bs-toggle', function (d) {
                        if (isSlotEditable(d.data, slotOptions))
                            return toggleModal && !slotOptions.notClickable ? 'modal' : null
                    })
                    .attr('data-bs-target', toggleModal && !slotOptions.notClickable ? '#modal' : null)
                    .attr('hx-target', toggleModal ? '#modal' : null)
                    .attr('hx-trigger', toggleModal ? 'click' : null)
                    .attr('data-toggle', 'virtual-popper')
                    .attr('data-virtual-popper-title', function (d) {
                        return getSlotTitle(d.data)
                    })
                    .attr('data-virtual-popper-content', function (d) {
                        if (slotOptions.showDnaSequence)
                            return getSlotContent(d.data)
                    })
                    .append('path')
                    .attr('d', function (d) {
                        let pos, pathList, step = 0.1;
                        d.endAngle -= padAngle;
                        d.startAngle += padAngle;
                        if (d.data.shape === 'arrow' && d.data.direction)
                            if (d.data.direction === 1) {
                                pos = arc.centroid({startAngle: d.endAngle, endAngle: d.endAngle});
                                d.endAngle = Math.max(d.startAngle, d.endAngle - step);
                                pathList = arc(d).split(/(?=[LMCAZ])/);
                                pathList[2] = 'L' + pos + pathList[2];
                            } else {
                                pos = arc.centroid({startAngle: d.startAngle, endAngle: d.startAngle});
                                d.startAngle = Math.min(d.endAngle, d.startAngle + step);
                                pathList = arc(d).split(/(?=[LMCAZ])/);
                                pathList[0] = 'M' + pos + pathList[0].replace('M', 'L');
                            }
                        else {
                            return arc(d);
                        }
                        return pathList.join(' ');
                    })
                    .attr('class', function (d) {
                        if (validationResults)
                            return validationColoring(d.data, validationResults);
                        return standardColoring(d.data, slotOptions, current);
                    });

                if (editable === true) {

                    let outerArc = d3.arc()
                        .outerRadius(r1 * 1.1)
                        .innerRadius(r1 * 1.1);

                    svg.append('g').attr('class', 'lines');
                    svg.select('.lines')
                        .selectAll('polyline')
                        .data(pie)
                        .enter()
                        .append('polyline')
                        .attr('points', function (d) {
                            let a = d.endAngle - halfPi,
                                x0 = r0 * cos(a),
                                y0 = r0 * sin(a),
                                x1 = radius * 0.9 * cos(a),
                                y1 = radius * 0.9 * sin(a);
                            return [[x0, y0], [x1, y1]]
                        });

                    svg.append('g').attr('class', 'slice-labels');
                    svg.select('.slice-labels').selectAll('text')
                        .data(pie)
                        .enter()
                        .append('text')
                        .attr('dy', '.35em')
                        .attr('data-slot-action', 'delete')
                        .attr('data-toggle', 'virtual-popper')
                        .attr('data-virtual-popper-content', function (d) {
                            return `Remove ${d.data.title}`
                        })
                        .text('-')
                        .attr('transform', function (d) {
                            return 'translate(' + outerArc.centroid(d) + ')';
                        });

                    svg.append('g').attr('class', 'line-labels');
                    svg.select('.line-labels').selectAll('text')
                        .data(pie)
                        .enter()
                        .append('text')
                        .attr('dy', '.35em')
                        .attr('data-slot-action', 'create')
                        .attr('data-toggle', 'virtual-popper')
                        .attr('data-virtual-popper-content', 'Add placeholder')
                        .text('+')
                        .attr("transform", function (d) {
                            let r = radius * 0.95,
                                pos = [r * Math.cos(d.endAngle - halfPi), r * Math.sin(d.endAngle - halfPi)];
                            return 'translate(' + pos + ')';
                        });

                }
            }
        );
    }

    // getter and setter functions. See Mike Bostocks post "Towards Reusable Charts" for a tutorial on how this works.
    chart.width = function (value) {
        if (!arguments.length) return width;
        width = value;
        return chart;
    };

    chart.height = function (value) {
        if (!arguments.length) return height;
        height = value;
        return chart;
    };

    chart.margin = function (value) {
        if (!arguments.length) return margin;
        margin = value;
        return chart;
    };
    chart.current = function (value) {
        if (!arguments.length) return current;
        current = value ? parseInt(value) : undefined;
        return chart;
    };

    chart.title = function (value) {
        if (!arguments.length) return title;
        title = value;
        return chart;
    };

    chart.padAngle = function (value) {
        if (!arguments.length) return padAngle;
        padAngle = value;
        return chart;
    };

    chart.validationResults = function (value) {
        if (!arguments.length) return validationResults;
        validationResults = value ? JSON.parse(value) : value;
        return chart;
    };

    chart.outerRadius = function (value) {
        if (!arguments.length) return outerRadius;
        outerRadius = value;
        return chart;
    };

    chart.innerRadius = function (value) {
        if (!arguments.length) return innerRadius;
        innerRadius = value;
        return chart;
    };

    chart.editable = function (value) {
        if (!arguments.length) return editable;
        editable = value !== undefined;
        return chart;
    };

    chart.dataUrl = function (value) {
        if (!arguments.length) return dataUrl;
        dataUrl = value;
        return chart;
    };


    chart.slotOptions = function (value) {
        if (!arguments.length) return slotOptions;
        slotOptions = value || {};
        return chart;
    };

    return chart;
}

export {
    isSlotEditable,
    validationColoring,
    standardColoring,
    sizeToValue,
    getSlotTitle,
    getSlotContent,
    plasmid,
}
